import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "company-profile-tab" }
const _hoisted_2 = {
  key: 0,
  class: "profile-loading"
}
const _hoisted_3 = {
  key: 1,
  class: "profile-error"
}
const _hoisted_4 = { class: "profile-error__message" }
const _hoisted_5 = {
  key: 2,
  class: "profile-empty"
}
const _hoisted_6 = {
  key: 3,
  class: "profile-card"
}
const _hoisted_7 = { class: "profile-header" }
const _hoisted_8 = ["href"]
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = { class: "profile-avatar__overlay" }
const _hoisted_11 = {
  key: 1,
  class: "profile-avatar"
}
const _hoisted_12 = { class: "profile-avatar__placeholder" }
const _hoisted_13 = { class: "profile-meta" }
const _hoisted_14 = {
  key: 0,
  class: "profile-industries"
}
const _hoisted_15 = {
  key: 1,
  class: "profile-location"
}
const _hoisted_16 = {
  key: 0,
  class: "profile-section"
}
const _hoisted_17 = { class: "profile-description" }
const _hoisted_18 = {
  key: 1,
  class: "profile-section"
}
const _hoisted_19 = {
  key: 2,
  class: "profile-section"
}
const _hoisted_20 = ["href"]
const _hoisted_21 = {
  key: 3,
  class: "profile-section"
}
const _hoisted_22 = { class: "profile-info-value" }
const _hoisted_23 = {
  key: 4,
  class: "profile-section"
}
const _hoisted_24 = { class: "profile-info-value" }
const _hoisted_25 = {
  key: 5,
  class: "profile-section"
}
const _hoisted_26 = { class: "profile-info-value" }
const _hoisted_27 = {
  key: 6,
  class: "profile-section"
}
const _hoisted_28 = { class: "profile-info-value" }
const _hoisted_29 = {
  key: 7,
  class: "profile-section"
}
const _hoisted_30 = { class: "specialties-container" }

import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";

interface ProfileData {
  companyName?: string;
  description?: string;
  industry?: string;
  website?: string;
  companyType?: string;
  employeeCount?: number | null;
  headquarters?: string;
  specialties?: string[];
  foundedYear?: number | null;
  logoUrl?: string;
  coverImageUrl?: string;
  followersCount?: number | null;
}

interface Props {
  profile?: ProfileData | null;
  loading?: boolean;
  error?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyProfileTab',
  props: {
    profile: { default: null },
    loading: { type: Boolean, default: false },
    error: { default: null }
  },
  emits: ["retry"],
  setup(__props: any) {

const props = __props;



const logoFailed = ref(false);
const onLogoError = () => {
  logoFailed.value = true;
};

function summaryToBullets(summary?: string): string[] {
  if (!summary) return [];
  const trimmed = summary.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\n")) {
    return trimmed
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 8);
  }
  const sentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (sentences.length <= 1) return [trimmed];
  return sentences.slice(0, 8);
}

const overview = computed(() => summaryToBullets(props.profile?.description));

// Parse industry field - handle both string and array formats
const industries = computed(() => {
  const industry = props.profile?.industry;
  if (!industry) return [];

  if (Array.isArray(industry)) {
    return industry.filter(Boolean).map((item) => String(item).trim());
  }

  const industryStr = String(industry).trim();
  if (industryStr.startsWith("[") && industryStr.endsWith("]")) {
    try {
      const parsed = JSON.parse(industryStr);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean).map((item) => String(item).trim());
      }
    } catch {
      const match = industryStr.match(/\["([^"]+)"\]/);
      if (match?.[1]) {
        return [match[1]];
      }
    }
  }

  return [industryStr];
});

const normalizeWebsite = (url?: string): string => {
  if (!url) return "";
  try {
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObj.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const formatEmployeeCount = (count?: number | null): string => {
  if (!count) return "";
  if (count >= 10000) return `${(count / 1000).toFixed(0)}k+ employees`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k employees`;
  return `${count} employees`;
};

const formatNumber = (num?: number | null): string => {
  if (!num) return "0";
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(LoadingBars, { size: "sm" })
        ]))
      : (__props.error)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(Icon), {
              icon: "mdi:alert-circle-outline",
              class: "profile-error__icon"
            }),
            _createElementVNode("p", _hoisted_4, _toDisplayString(__props.error), 1),
            _createVNode(Button, {
              variant: "primary",
              size: "sm",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('retry')))
            }, {
              default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                _createTextVNode("Try Again", -1)
              ]))]),
              _: 1
            })
          ]))
        : (!__props.profile)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(Icon), {
                icon: "mdi:office-building-outline",
                class: "profile-empty__icon"
              }),
              _cache[2] || (_cache[2] = _createElementVNode("p", null, "No company profile data available", -1))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                (__props.profile.logoUrl && !logoFailed.value)
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 0,
                      href: __props.profile.logoUrl,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      class: "profile-avatar profile-avatar--clickable"
                    }, [
                      _createElementVNode("img", {
                        src: __props.profile.logoUrl,
                        alt: __props.profile.companyName || 'Company logo',
                        onError: onLogoError
                      }, null, 40, _hoisted_9),
                      _createElementVNode("div", _hoisted_10, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:open-in-new",
                          class: "profile-avatar__icon"
                        })
                      ])
                    ], 8, _hoisted_8))
                  : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:office-building",
                          width: "28",
                          height: "28"
                        })
                      ])
                    ])),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("h5", null, _toDisplayString(__props.profile.companyName || ""), 1),
                  (industries.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(industries.value, (industry, index) => {
                          return (_openBlock(), _createElementBlock("span", {
                            key: index,
                            class: "industry-chip"
                          }, _toDisplayString(industry), 1))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true),
                  (__props.profile.headquarters)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(__props.profile.headquarters), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              (__props.profile.description)
                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                    _cache[3] || (_cache[3] = _createElementVNode("h6", null, "ABOUT", -1)),
                    _createElementVNode("div", _hoisted_17, _toDisplayString(__props.profile.description), 1)
                  ]))
                : _createCommentVNode("", true),
              (overview.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    _cache[4] || (_cache[4] = _createElementVNode("h6", null, "OVERVIEW", -1)),
                    _createElementVNode("ul", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(overview.value, (item, index) => {
                        return (_openBlock(), _createElementBlock("li", { key: index }, _toDisplayString(item), 1))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true),
              (__props.profile.website)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _cache[5] || (_cache[5] = _createElementVNode("h6", null, "WEBSITE", -1)),
                    _createElementVNode("a", {
                      href: __props.profile.website,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      class: "profile-website-link"
                    }, [
                      _createTextVNode(_toDisplayString(normalizeWebsite(__props.profile.website)) + " ", 1),
                      _createVNode(_unref(Icon), { icon: "mdi:open-in-new" })
                    ], 8, _hoisted_20)
                  ]))
                : _createCommentVNode("", true),
              (__props.profile.employeeCount)
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                    _cache[6] || (_cache[6] = _createElementVNode("h6", null, "COMPANY SIZE", -1)),
                    _createElementVNode("p", _hoisted_22, _toDisplayString(formatEmployeeCount(__props.profile.employeeCount)), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.profile.foundedYear)
                ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    _cache[7] || (_cache[7] = _createElementVNode("h6", null, "FOUNDED", -1)),
                    _createElementVNode("p", _hoisted_24, _toDisplayString(__props.profile.foundedYear), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.profile.companyType)
                ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                    _cache[8] || (_cache[8] = _createElementVNode("h6", null, "COMPANY TYPE", -1)),
                    _createElementVNode("p", _hoisted_26, _toDisplayString(__props.profile.companyType), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.profile.followersCount !== null && __props.profile.followersCount !== undefined)
                ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                    _cache[9] || (_cache[9] = _createElementVNode("h6", null, "FOLLOWERS", -1)),
                    _createElementVNode("p", _hoisted_28, _toDisplayString(formatNumber(__props.profile.followersCount)), 1)
                  ]))
                : _createCommentVNode("", true),
              (__props.profile.specialties && __props.profile.specialties.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                    _cache[10] || (_cache[10] = _createElementVNode("h6", null, "SPECIALTIES", -1)),
                    _createElementVNode("div", _hoisted_30, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.profile.specialties, (specialty, index) => {
                        return (_openBlock(), _createElementBlock("span", {
                          key: index,
                          class: "specialty-tag"
                        }, _toDisplayString(specialty), 1))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]))
  ]))
}
}

})